import React from 'react';
import { Box, FormControlLabel, Switch, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { MagicReportSection } from './MagicReportSection';
import {
  PLAN_COST_REVIEW_INITIAL_REVIEW_FIELD,
  PLAN_COST_REVIEW_SECTION_CONFIG,
  PLAN_COST_REVIEW_TOGGLE_OPTIONS,
  PlanCostReviewStatusOption,
  getPlanCostReviewDefaultTexts,
} from './sections.config';
import { DeclarativeLayoutRenderer, DeclarativeLayoutConfig, FieldConfig } from './DeclarativeLayoutRenderer';
import { CommentsSection } from './CommentsSection';
import { getCommentsField } from './section-utils';
import { SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { useMagicReport } from '../context/MagicReportContext';

interface PlanCostReviewIssuesSectionProps {
  magicReportId: string;
}

interface PlanCostReviewIssuesSectionContentProps {
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  reviewingPlanOrCost?: boolean | null;
  onBook?: () => number;
}

const PlanCostReviewIssuesSectionContent: React.FC<PlanCostReviewIssuesSectionContentProps> = ({
  data,
  handleFieldChange,
  getField,
  reviewingPlanOrCost,
  onBook,
}) => {
  const { registerSectionSync, unregisterSectionSync } = useMagicReport();
  const statusField = getField('PlanCostReviewStatus');
  const selectedStatus = statusField?.value || '';
  const commentsField = getCommentsField(data, PLAN_COST_REVIEW_SECTION_CONFIG.config);
  const typedStatus = PLAN_COST_REVIEW_TOGGLE_OPTIONS.find(option => option === selectedStatus);
  const primaryField = getField('PlanCostReviewPrimaryText');
  const secondaryField = getField('PlanCostReviewSecondaryText');
  const isPlanCostReviewCompleted = reviewingPlanOrCost === true;
  // Missing on reports saved before the toggle existed — treat as on so their text is left as it was.
  const isInitialReview = getField(PLAN_COST_REVIEW_INITIAL_REVIEW_FIELD)?.value !== 'false';
  const previousCompletionRef = React.useRef(reviewingPlanOrCost);
  const lastSyncedPlanValueRef = React.useRef<boolean | null | undefined>(undefined);

  React.useEffect(() => {
    // Only treat as a real change when going from one defined value to a different defined value.
    // Ignoring undefined → value transitions prevents auto-populating over retained text
    // when reviewingPlanOrCost loads asynchronously after mount.
    const planChanged = previousCompletionRef.current !== undefined && previousCompletionRef.current !== reviewingPlanOrCost;
    previousCompletionRef.current = reviewingPlanOrCost;

    const defaultStatus = reviewingPlanOrCost ? PLAN_COST_REVIEW_TOGGLE_OPTIONS[0] : '';
    const statusToApply = reviewingPlanOrCost ? typedStatus || defaultStatus : '';
    const { primaryText, secondaryText } = getPlanCostReviewDefaultTexts(
      reviewingPlanOrCost,
      reviewingPlanOrCost ? (statusToApply as PlanCostReviewStatusOption) : undefined,
      isInitialReview,
    );
    const resolvedSecondary = reviewingPlanOrCost ? secondaryText || '' : '';

    const isInitialSync = lastSyncedPlanValueRef.current === undefined;
    // On initial load, only auto-populate if there is no saved primary text.
    // When the flag genuinely changes, always reset to the appropriate defaults.
    const shouldSync = planChanged || (isInitialSync && !primaryField?.value);

    if (!shouldSync) {
      if (isInitialSync) {
        lastSyncedPlanValueRef.current = reviewingPlanOrCost;
      }
      return;
    }

    const pendingUpdates: Array<[string, Partial<SectionFieldDTO>]> = [];

    if ((statusField?.value || '') !== statusToApply) {
      pendingUpdates.push(['PlanCostReviewStatus', { value: statusToApply }]);
    }

    if (primaryField?.value !== primaryText) {
      pendingUpdates.push(['PlanCostReviewPrimaryText', { value: primaryText }]);
    }

    if ((secondaryField?.value || '') !== resolvedSecondary) {
      pendingUpdates.push(['PlanCostReviewSecondaryText', { value: resolvedSecondary }]);
    }

    pendingUpdates.forEach(([field, update]) => handleFieldChange(field, update));
    lastSyncedPlanValueRef.current = reviewingPlanOrCost;
  }, [
    reviewingPlanOrCost,
    typedStatus,
    isInitialReview,
    statusField?.value,
    primaryField?.value,
    secondaryField?.value,
    handleFieldChange,
  ]);

  const handleRestoreDefaultText = (status?: PlanCostReviewStatusOption, initialReview = isInitialReview) => {
    const { primaryText, secondaryText } = getPlanCostReviewDefaultTexts(reviewingPlanOrCost, status, initialReview);

    handleFieldChange('PlanCostReviewPrimaryText', { value: primaryText });
    handleFieldChange('PlanCostReviewSecondaryText', { value: secondaryText || '' });
  };

  const handleInitialReviewChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    handleFieldChange(PLAN_COST_REVIEW_INITIAL_REVIEW_FIELD, { value: checked ? 'true' : 'false' });
    handleRestoreDefaultText(typedStatus, checked);
  };

  const handleStatusChange = (_: React.MouseEvent<HTMLElement>, newStatus: PlanCostReviewStatusOption | null) => {
    if (!newStatus || newStatus === selectedStatus) {
      return;
    }

    handleFieldChange('PlanCostReviewStatus', { value: newStatus });
    handleRestoreDefaultText(newStatus);
  };

  const restorePrimaryText = () => {
    const { primaryText } = getPlanCostReviewDefaultTexts(reviewingPlanOrCost, typedStatus, isInitialReview);
    handleFieldChange('PlanCostReviewPrimaryText', { value: primaryText });
  };

  const restoreSecondaryText = () => {
    const { secondaryText } = getPlanCostReviewDefaultTexts(reviewingPlanOrCost, typedStatus, isInitialReview);
    handleFieldChange('PlanCostReviewSecondaryText', { value: secondaryText || '' });
  };

  // Register sync function to be called when "Run this Month" is clicked
  React.useEffect(() => {
    const syncFn = async () => {
      restorePrimaryText();
      if (isPlanCostReviewCompleted) {
        restoreSecondaryText();
      }
    };

    registerSectionSync('planCostReviewIssues', syncFn);
    return () => {
      unregisterSectionSync('planCostReviewIssues');
    };
  }, [registerSectionSync, unregisterSectionSync, reviewingPlanOrCost, typedStatus, isInitialReview, handleFieldChange]);

  // With the initial review off the primary comment is intentionally empty, so its box is hidden.
  const singleColumnFields: FieldConfig[] =
    isPlanCostReviewCompleted && !isInitialReview
      ? []
      : [
          {
            fieldName: 'PlanCostReviewPrimaryText',
            type: 'text-with-refresh',
            label: '',
            onRefresh: restorePrimaryText,
          },
        ];

  if (isPlanCostReviewCompleted) {
    singleColumnFields.push({
      fieldName: 'PlanCostReviewSecondaryText',
      type: 'text-with-refresh',
      label: '',
      onRefresh: restoreSecondaryText,
    });
  }

  const layout: DeclarativeLayoutConfig = {
    singleColumn: singleColumnFields,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Box sx={{ flexGrow: 1, width: '100%' }}>
              <ToggleButtonGroup
                exclusive
                size='medium'
                value={selectedStatus || null}
                onChange={handleStatusChange}
                disabled={!isPlanCostReviewCompleted}
                fullWidth
                sx={{
                  '& .MuiToggleButton-root': {
                    flex: 1,
                    py: 1.5,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  },
                }}
              >
                {PLAN_COST_REVIEW_TOGGLE_OPTIONS.map(option => (
                  <ToggleButton key={option} value={option}>
                    {option}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
          {/* Reserve space matching [refresh icon 20px] + [gap 8px] + [bookmark icon 20px] + [gap 8px] = 56px */}
          <Box sx={{ width: 56, flexShrink: 0 }} />
        </Box>
        {isPlanCostReviewCompleted && (
          <FormControlLabel
            control={<Switch checked={isInitialReview} onChange={handleInitialReviewChange} />}
            label='Initial review (first draw)'
          />
        )}
      </Box>

      <DeclarativeLayoutRenderer layout={layout} getField={getField} handleFieldChange={handleFieldChange} onBook={onBook} />

      {commentsField && (
        <CommentsSection
          commentsField={commentsField}
          onChange={updated => handleFieldChange(commentsField.fieldName, updated)}
          title='Comments'
          onBook={onBook}
        />
      )}
    </Box>
  );
};

export const PlanCostReviewIssuesSection: React.FC<PlanCostReviewIssuesSectionProps> = ({ magicReportId }) => {
  const { reportState } = useMagicReport();
  const reviewingPlanOrCost = reportState.projectSummary?.reviewingPlanOrCost;

  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={PLAN_COST_REVIEW_SECTION_CONFIG}
      customization={{
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <PlanCostReviewIssuesSectionContent
            data={data}
            handleFieldChange={handleFieldChange}
            getField={getField}
            reviewingPlanOrCost={reviewingPlanOrCost}
            onBook={onBook}
          />
        ),
      }}
    />
  );
};
